import { useCallback } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { cn } from '@/true-fsd/shared/utils/css'
import type { FontFamily } from '../types'
import { AVAILABLE_FONTS } from '../constants'
import classes from './FontSelectorPopup.module.scss'

/**
 * Пропсы для компонента FontSelectorPopup
 */
export interface FontSelectorPopupProps {
    /**
     * Текущий выбранный шрифт
     */
    value: FontFamily
    /**
     * Обработчик изменения шрифта
     */
    onChange: (font: FontFamily) => void
    /**
     * Дополнительные CSS классы
     */
    className?: string
    /**
     * Дочерний элемент, который будет триггером попапа
     */
    children: React.ReactNode
    /**
     * Открыт ли попап (контролируемое состояние)
     */
    open?: boolean
    /**
     * Обработчик изменения состояния открытия
     */
    onOpenChange?: (open: boolean) => void
}

/**
 * Компонент попапа выбора шрифта
 * Отображает список из 4 доступных шрифтов: Inter, Onest, Times, Montserrat
 */
export function FontSelectorPopup({
    value,
    onChange,
    className,
    children,
    open,
    onOpenChange,
}: FontSelectorPopupProps) {
    /**
     * Обработчик выбора шрифта
     */
    const handleFontSelect = useCallback(
        (font: FontFamily) => {
            onChange(font)
            // Закрываем попап после выбора
            if (onOpenChange) {
                onOpenChange(false)
            }
        },
        [onChange, onOpenChange]
    )

    /**
     * Получить CSS класс для шрифта
     */
    const getFontClassName = useCallback((font: FontFamily) => {
        return classes[`font${font}` as keyof typeof classes]
    }, [])

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>{children}</PopoverTrigger>
            <PopoverContent
                align="start"
                side="top"
                sideOffset={4}
                className={cn(classes.popup, className)}
            >
                <div className={classes.fontList}>
                    {AVAILABLE_FONTS.map((font) => {
                        const isSelected = font === value
                        return (
                            <button
                                key={font}
                                type="button"
                                className={cn(classes.fontItem, {
                                    [classes.fontItemSelected]: isSelected,
                                })}
                                onClick={() => handleFontSelect(font)}
                                aria-label={`Выбрать шрифт ${font}`}
                                aria-pressed={isSelected}
                            >
                                <span
                                    className={cn(
                                        classes.fontName,
                                        getFontClassName(font)
                                    )}
                                >
                                    {font}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </PopoverContent>
        </Popover>
    )
}

